import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Chip,
  useTheme,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Avatar,
  Badge,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Menu,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import {
  getConversations,
  getMessages,
  sendMessage,
  startConversation,
  markAsRead,
  setActiveConversation,
} from '../store/slices/chatSlice';
import { Conversation, Message, MessageCreateInput } from '../store/slices/chatSlice';
import { fetchClients } from '../store/slices/clientsSlice';
import usersService from '../services/usersService';

type ChatParticipantOption = { id: number; name: string; kind: 'user' | 'client' };

const Chat: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { conversations, activeConversation, messages, loading, error } = useSelector((state: RootState) => state.chat);
  const { user } = useSelector((state: RootState) => state.auth);
  const { clients } = useSelector((state: RootState) => state.clients);

  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // New Conversation dialog
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [newConvKind, setNewConvKind] = useState<'client' | 'lawyer' | 'group'>('lawyer');
  const [newConvSelected, setNewConvSelected] = useState<ChatParticipantOption[]>([]);
  const [newConvTitle, setNewConvTitle] = useState('');
  const [newConvStarting, setNewConvStarting] = useState(false);
  const [staffOptions, setStaffOptions] = useState<ChatParticipantOption[]>([]);

  useEffect(() => {
    if (user?.user_id) {
      dispatch(getConversations(user.user_id.toString()));
    }
  }, [dispatch, user]);

  useEffect(() => {
    // Load once, lazily, the first time the "New Conversation" menu is used
    // rather than on every page visit.
    if (!newConvOpen) return;
    if (staffOptions.length === 0) {
      usersService.getUsers()
        .then((res: any) => {
          const list = res?.data || res || [];
          const currentId = String(user?.user_id ?? '');
          setStaffOptions(
            list
              .filter((u: any) => String(u.user_id ?? u.id) !== currentId)
              .map((u: any) => ({ id: u.user_id ?? u.id, name: u.full_name || u.email, kind: 'user' as const }))
          );
        })
        .catch((err) => console.error('Failed to load staff for chat:', err));
    }
    if (clients.length === 0 && user?.user_id) {
      dispatch(fetchClients(user.user_id.toString()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newConvOpen]);

  useEffect(() => {
    if (activeConversation) {
      dispatch(getMessages({ conversationId: activeConversation.id, userId: user?.user_id?.toString() || '' }));
      dispatch(markAsRead({ messageId: activeConversation.id, userId: user?.user_id?.toString() || '' }));
    }
  }, [dispatch, activeConversation, user]);

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    try {
      await dispatch(sendMessage({
        conversation_id: activeConversation.id,
        sender_id: user?.user_id || '',
        content: newMessage.trim(),
      }));
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleConversationClick = (conversation: Conversation) => {
    dispatch(setActiveConversation(conversation));
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      // In real implementation, this would search via API
      console.log('Searching for:', query);
    } else {
      // Reset to show all conversations
      dispatch(getConversations(user?.user_id?.toString() || ''));
    }
  };

  const clientOptions: ChatParticipantOption[] = clients.map((c) => ({
    id: c.id,
    name: `${c.first_name} ${c.last_name}`.trim() || c.company,
    kind: 'client' as const,
  }));

  const openNewConversation = (kind: 'client' | 'lawyer' | 'group') => {
    setAnchorEl(null);
    setNewConvKind(kind);
    setNewConvSelected([]);
    setNewConvTitle('');
    setNewConvOpen(true);
  };

  const toggleParticipant = (option: ChatParticipantOption) => {
    if (newConvKind === 'group') {
      setNewConvSelected((prev) =>
        prev.some((p) => p.id === option.id && p.kind === option.kind)
          ? prev.filter((p) => !(p.id === option.id && p.kind === option.kind))
          : [...prev, option]
      );
    } else {
      // Direct chats (client/lawyer) have exactly one other participant.
      setNewConvSelected([option]);
    }
  };

  const handleStartConversation = async () => {
    if (newConvSelected.length === 0 || !user?.user_id) return;

    const title = newConvKind === 'group'
      ? (newConvTitle.trim() || newConvSelected.map((p) => p.name).join(', '))
      : newConvSelected[0].name;

    try {
      setNewConvStarting(true);
      await dispatch(startConversation({
        title,
        type: newConvKind === 'group' ? 'group' : 'direct',
        user_id: user.user_id,
        participants: [user.user_id, ...newConvSelected.map((p) => p.id)],
      })).unwrap();
      setNewConvOpen(false);
    } catch (err) {
      console.error('Failed to start conversation:', err);
    } finally {
      setNewConvStarting(false);
    }
  };

  // The backend nests the profile under participant.user (see the
  // /chat/start response), not flat first_name/last_name on the
  // participant itself — falling back to those keeps this working if an
  // older/different endpoint ever returns the flatter shape.
  const getParticipantName = (p: any) =>
    p.user?.full_name
    || [p.first_name, p.last_name].filter(Boolean).join(' ')
    || p.user?.email
    || p.email
    || 'Unknown';

  const getConversationName = (conversation: Conversation) => {
    if (conversation.type === 'group' && conversation.title) return conversation.title;
    if (!conversation.participants?.length) return conversation.title || 'Unknown';

    const otherParticipants = conversation.participants.filter((p: any) => p.user_id !== user?.user_id);
    if (otherParticipants.length === 0) return 'You';
    if (otherParticipants.length === 1) {
      return getParticipantName(otherParticipants[0]);
    }
    return `${otherParticipants.length} participants`;
  };

  const getConversationInitials = (conversation: Conversation) => {
    const name = getConversationName(conversation);
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]?.toUpperCase())
      .join('') || '?';
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    const matchesSearch = searchQuery.trim()
      ? (conv.title || '').toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    // conv.participants can legitimately be absent (list endpoints don't
    // always hydrate it) — filtering on tabs 1/2 must not crash when it is.
    if (activeTab === 0) return matchesSearch; // All conversations
    if (activeTab === 1) return matchesSearch && (conv.participants?.length ?? 0) <= 2; // Direct messages
    if (activeTab === 2) return matchesSearch && (conv.participants?.length ?? 0) > 2; // Group chats

    return matchesSearch;
  });

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography>Loading conversations...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Messages</Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAnchorEl(anchorEl ? null : document.body)}
            >
              New Conversation
            </Button>
          </Box>
          
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
        {/* Conversations List */}
        <Card sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          <CardContent>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
              <Tab label="All" />
              <Tab label="Direct" />
              <Tab label="Groups" />
            </Tabs>
            
            <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
              {filteredConversations.map((conv) => (
                <ListItem key={conv.id} disablePadding>
                  <ListItemButton
                    selected={activeConversation?.id === conv.id}
                    onClick={() => handleConversationClick(conv)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: conv.unread_count ? 'error.main' : 'transparent',
                            boxShadow: `0 0 2px ${conv.unread_count ? 'error.main' : 'transparent'}`,
                          },
                        }}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '0.85rem' }}>
                          {getConversationInitials(conv)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2">
                            {getConversationName(conv)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(conv.last_message_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                            {conv.last_message}
                          </Typography>
                          {conv.unread_count && conv.unread_count > 0 && (
                            <Badge badgeContent={conv.unread_count} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: 18, height: 18 } }} />
                          )}
                        </Box>
                      }
                    />
                </ListItemButton>
              </ListItem>
            ))}
            </List>
          </CardContent>
        </Card>

        {/* Chat Area */}
        {activeConversation ? (
          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent>
              {/* Header */}
              <Box sx={{ p: 2, borderBottom: `1px solid #e0e0e0`, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '0.85rem' }}>
                  {getConversationInitials(activeConversation)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {getConversationName(activeConversation)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeConversation.participants?.length} participants
                  </Typography>
                </Box>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {messages[activeConversation.id]?.map((msg) => (
                  <Box key={msg.id} sx={{
                    display: 'flex',
                    justifyContent: msg.sender_id === user?.user_id ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}>
                    <Box sx={{
                      maxWidth: '70%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: msg.sender_id === user?.user_id ? 'primary.main' : 'grey.100',
                      color: msg.sender_id === user?.user_id ? 'white' : 'text.primary',
                    }}>
                      <Typography variant="body2">{msg.content}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', textAlign: 'right', mt: 0.5 }}>
                        {formatTime(msg.created_at || '')}
                      </Typography> 
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Input */}
              <Box sx={{ p: 2, borderTop: `1px solid #e0e0e0`, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <IconButton size="small">
                  <AttachFileIcon />
                </IconButton>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                <IconButton color="primary" onClick={handleSend} disabled={!newMessage.trim()}>
                  <SendIcon />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Select a conversation to start messaging
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* New Conversation Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => openNewConversation('client')}>
            <PersonAddIcon sx={{ mr: 1 }} />
            New Client Chat
          </MenuItem>
          <MenuItem onClick={() => openNewConversation('lawyer')}>
            <BusinessIcon sx={{ mr: 1 }} />
            New Lawyer Chat
          </MenuItem>
          <MenuItem onClick={() => openNewConversation('group')}>
            <GroupIcon sx={{ mr: 1 }} />
            New Group Chat
          </MenuItem>
        </Menu>
      </Box>

      {/* New Conversation Dialog */}
      <Dialog open={newConvOpen} onClose={() => setNewConvOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {newConvKind === 'client' ? 'New Client Chat' : newConvKind === 'lawyer' ? 'New Lawyer Chat' : 'New Group Chat'}
        </DialogTitle>
        <DialogContent>
          {newConvKind === 'group' && (
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Group name (optional)"
              value={newConvTitle}
              onChange={(e) => setNewConvTitle(e.target.value)}
              sx={{ mb: 1 }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {newConvKind === 'group' ? 'Select participants' : 'Select a person to message'}
          </Typography>
          <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {(newConvKind === 'client' ? clientOptions : newConvKind === 'lawyer' ? staffOptions : [...staffOptions, ...clientOptions]).map((option) => {
              const selected = newConvSelected.some((p) => p.id === option.id && p.kind === option.kind);
              return (
                <ListItem key={`${option.kind}-${option.id}`} disablePadding>
                  <ListItemButton selected={selected} onClick={() => toggleParticipant(option)}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: option.kind === 'client' ? 'secondary.main' : 'primary.main' }}>
                        {option.name?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={option.name} secondary={option.kind === 'client' ? 'Client' : 'Staff'} />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {(newConvKind === 'client' ? clientOptions : newConvKind === 'lawyer' ? staffOptions : [...staffOptions, ...clientOptions]).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                No one available to message yet.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewConvOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStartConversation}
            disabled={newConvStarting || newConvSelected.length === 0}
          >
            {newConvStarting ? 'Starting…' : 'Start Chat'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => dispatch({ type: 'chat/clearError' })}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Chat;
